import { FC, useCallback, useEffect } from 'react';
import { useDemoPanelShallowStore, useIsInsideDemoPanel } from '@frontend/demo-panels';
import { useTranslation } from '@frontend/i18n';
import { WIDTH_DIMS } from '@frontend/panel-engine';
import { CallInsightPanelSource } from '../../types';
import { DemoCallInsightPanelComponent } from '../demo-call-insight/demo-call-insight-panel-component';
import { CallIntelTrialTeaserAction } from './call-intel-trial-teaser-action';
import { CallIntelTrialTeaserPromotion, CallIntelTrialTeaserPromotionProps } from './call-intel-trial-teaser-promotion';

export const DEMO_CALL_INSIGHT_PANEL_ID = 'demo-call-insight';

export type CallIntelTrialTeaserWithDemoProps = Omit<CallIntelTrialTeaserPromotionProps, 'onViewDemo'> & {
  source?: CallInsightPanelSource;
};

/**
 * Wraps the Call Intelligence trial teaser and owns the "View Demo" flow: it registers the demo call insight panel in
 * the demo panel drawer (stacked above the current call insight panel), sets the drawer banner and cleans up on unmount.
 *
 * The demo call lives in its own call insight context (defaults to `callInsightTrialTeaser`), so it never touches the real call.
 */
export const CallIntelTrialTeaserWithDemo: FC<CallIntelTrialTeaserWithDemoProps> = ({
  locationId,
  source,
  ...rest
}) => {
  const { t } = useTranslation('call-insight-panel');
  const isInsideDemoPanel = useIsInsideDemoPanel();
  const {
    register: registerInDemoPanel,
    unregister: unregisterFromDemoPanel,
    setHeaderConfig: setDemoPanelHeaderConfig,
  } = useDemoPanelShallowStore('register', 'unregister', 'setHeaderConfig');

  const handleViewDemo = useCallback(() => {
    if (!locationId) return;

    setDemoPanelHeaderConfig({
      action: {
        render: (
          <CallIntelTrialTeaserAction
            locationId={locationId}
            source='ci-insights-demo-panel'
            trackingPrefix='demo-call-insight'
            testIdPrefix='demo-call-insight'
          />
        ),
      },
      iconName: 'premium-badge',
      title: t('This is sample data.'),
      description: t('No card required to start your trial.'),
    });

    registerInDemoPanel({
      id: DEMO_CALL_INSIGHT_PANEL_ID,
      component: DemoCallInsightPanelComponent,
      componentProps: { demoLocationId: locationId, source },
      width: WIDTH_DIMS.medium,
    });
  }, [locationId, registerInDemoPanel, setDemoPanelHeaderConfig, source, t]);

  // The demo screen renders this wrapper too, and its unmount must not close the demo panel it lives in.
  useEffect(() => {
    if (isInsideDemoPanel) return;
    return () => {
      unregisterFromDemoPanel(DEMO_CALL_INSIGHT_PANEL_ID);
    };
  }, [isInsideDemoPanel, unregisterFromDemoPanel]);

  return <CallIntelTrialTeaserPromotion {...rest} locationId={locationId} onViewDemo={handleViewDemo} />;
};
